import { useState } from "react";
import { Link } from "react-router-dom";
import SimpleSalesReport from "./components/SimpleSalesReport";
import ProductPurchaseSaleReport from "./components/ProductPurchaseSaleReport";
import CustomerBasedSalesReport from "./components/CustomerBasedSalesReport";
import SalesLossReport from "./components/SalesLossReport";
import PurchasesReport from "./components/PurchasesReport";
import ReturnsReport from "./components/ReturnsReport";
import OffersReport from "./components/OffersReport";
import SixMonthSalesReport from "./components/SixMonthSalesReport";
import StockSalesMatchReport from "./components/StockSalesMatchReport";
import CategoryBasedSalesReport from "./components/CategoryBasedSalesReport";

interface ReportCard {
  id: string;
  title: string;
  icon: string;
  color: string;
  hoverColor: string;
  textColor: string;
}

const cards: ReportCard[] = [
  {
    id: "simple-sales",
    title: "BASİT SATIŞ RAPORU",
    icon: "ri-shopping-cart-2-line",
    color: "bg-primary-600",
    hoverColor: "hover:bg-primary-700",
    textColor: "text-background-50",
  },
  {
    id: "product-purchase-sale",
    title: "ÜRÜN ALIŞ-SATIŞ RAPORU",
    icon: "ri-archive-line",
    color: "bg-accent-700",
    hoverColor: "hover:bg-accent-800",
    textColor: "text-background-50",
  },
  {
    id: "customer-based",
    title: "CARİ BAZLI SATIŞ RAPORU",
    icon: "ri-group-line",
    color: "bg-secondary-600",
    hoverColor: "hover:bg-secondary-700",
    textColor: "text-background-50",
  },
  {
    id: "sales-loss",
    title: "SATIŞ KAYBI",
    icon: "ri-thumb-down-line",
    color: "bg-accent-500",
    hoverColor: "hover:bg-accent-600",
    textColor: "text-background-50",
  },
  {
    id: "purchases",
    title: "ALIŞLAR",
    icon: "ri-arrow-down-line",
    color: "bg-secondary-500",
    hoverColor: "hover:bg-secondary-600",
    textColor: "text-background-50",
  },
  {
    id: "returns",
    title: "İADELER",
    icon: "ri-arrow-go-back-line",
    color: "bg-foreground-700",
    hoverColor: "hover:bg-foreground-800",
    textColor: "text-background-50",
  },
  {
    id: "offers",
    title: "TEKLİFLER",
    icon: "ri-file-list-3-line",
    color: "bg-secondary-400",
    hoverColor: "hover:bg-secondary-500",
    textColor: "text-background-50",
  },
  {
    id: "six-month",
    title: "6 AYLIK SATIŞLAR",
    icon: "ri-bar-chart-line",
    color: "bg-primary-500",
    hoverColor: "hover:bg-primary-600",
    textColor: "text-background-50",
  },
  {
    id: "stock-sales",
    title: "STOK-SATIŞ KARŞILAMA",
    icon: "ri-arrow-left-right-line",
    color: "bg-accent-600",
    hoverColor: "hover:bg-accent-700",
    textColor: "text-background-50",
  },
  {
    id: "category-based",
    title: "Kategori Bazlı Satış Raporu",
    icon: "ri-pie-chart-line",
    color: "bg-accent-800",
    hoverColor: "hover:bg-accent-900",
    textColor: "text-background-50",
  },
];

export default function SalesPurchasesReportsPage() {
  const [activeCard, setActiveCard] = useState<string | null>(null);

  const handleCardClick = (id: string) => {
    setActiveCard(id === activeCard ? null : id);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground-950">Satış - Alış Raporları</h1>
        <p className="text-sm text-foreground-500 mt-0.5">
          Satış ve alış raporları menüsünden görüntülemek istediğiniz raporu seçin
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Row 1: Basit Satış (col-span-2) + Ürün Alış-Satış (col-span-1) + Cari Bazlı (col-span-1) */}
        <button
          onClick={() => handleCardClick(cards[0].id)}
          className={`${cards[0].color} ${cards[0].hoverColor} ${cards[0].textColor} col-span-1 sm:col-span-1 lg:col-span-2 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
            <i className={`${cards[0].icon} text-3xl sm:text-4xl`}></i>
          </div>
          <span className="text-sm sm:text-base font-bold tracking-wide text-center">
            {cards[0].title}
          </span>
        </button>

        {cards.slice(1, 3).map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`${card.color} ${card.hoverColor} ${card.textColor} col-span-1 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <i className={`${card.icon} text-3xl sm:text-4xl`}></i>
            </div>
            <span className="text-sm sm:text-base font-bold tracking-wide text-center">
              {card.title}
            </span>
          </button>
        ))}

        {/* Row 2: Satış Kaybı + Alışlar + İadeler + Teklifler */}
        {cards.slice(3, 7).map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`${card.color} ${card.hoverColor} ${card.textColor} col-span-1 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <i className={`${card.icon} text-3xl sm:text-4xl`}></i>
            </div>
            <span className="text-sm sm:text-base font-bold tracking-wide text-center">
              {card.title}
            </span>
          </button>
        ))}

        {/* Row 3: 6 Aylık Satışlar (col-span-2) + Stok-Satış (col-span-1) + Kategori Bazlı (col-span-1) */}
        <button
          onClick={() => handleCardClick(cards[7].id)}
          className={`${cards[7].color} ${cards[7].hoverColor} ${cards[7].textColor} col-span-1 sm:col-span-1 lg:col-span-2 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
            <i className={`${cards[7].icon} text-3xl sm:text-4xl`}></i>
          </div>
          <span className="text-sm sm:text-base font-bold tracking-wide text-center">
            {cards[7].title}
          </span>
        </button>

        {cards.slice(8).map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`${card.color} ${card.hoverColor} ${card.textColor} col-span-1 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-4 transition-all duration-200 cursor-pointer active:scale-[0.98] min-h-[140px] sm:min-h-[160px]`}
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
              <i className={`${card.icon} text-3xl sm:text-4xl`}></i>
            </div>
            <span className="text-sm sm:text-base font-bold tracking-wide text-center">
              {card.title}
            </span>
          </button>
        ))}
      </div>

      {/* Active Report Content */}
      {activeCard === "simple-sales" && <SimpleSalesReport />}
      {activeCard === "product-purchase-sale" && <ProductPurchaseSaleReport />}
      {activeCard === "customer-based" && <CustomerBasedSalesReport />}
      {activeCard === "sales-loss" && <SalesLossReport />}
      {activeCard === "purchases" && <PurchasesReport />}
      {activeCard === "returns" && <ReturnsReport />}
      {activeCard === "offers" && <OffersReport />}
      {activeCard === "six-month" && <SixMonthSalesReport />}
      {activeCard === "stock-sales" && <StockSalesMatchReport />}
      {activeCard === "category-based" && <CategoryBasedSalesReport />}

      {/* Back link */}
      <div className="flex justify-start pt-2">
        <Link
          to="/reports"
          className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Raporlara Dön
        </Link>
      </div>
    </div>
  );
}